import { useState } from "react";
import { useSelector } from "react-redux";

import {
  readItems,
} from "@directus/sdk";
import { message, Modal } from "antd";
import { getDirectusClient } from "../../../../lib/directus";
import { truncateSerial } from "../../../../lib/utils";


const callRepayAPI = async (endpoint, data) => {
  try {
    const baseUrl = process.env.REACT_APP_URL_COMPLEMENTO || '';
    const fullUrl = `${baseUrl}${endpoint}`;
    
    console.log('Calling API:', fullUrl);
    console.log('Request data:', data);
    
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response body:', errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log('API Response:', result);
    
    // Verificar si la respuesta es un array (seriales no encontrados)
    if (Array.isArray(result)) {
      throw new Error(`Seriales no encontrados: ${result.join(', ')}`);
    }

    // Verificar si la respuesta contiene notFoundSerials
    if (result.notFoundSerials && Array.isArray(result.notFoundSerials)) {
      const totalSent = data.serials ? data.serials.length : 0;
      const successfullyReturned = totalSent - result.notFoundSerials.length;
      
      return {
        ...result,
        successfullyReturned,
        notFoundCount: result.notFoundSerials.length,
        totalSent
      };
    }

    return result;
  } catch (error) {
    console.error('Error calling repay API:', error);
    throw error;
  }
};

export function useSerials({ vendor, loans, onLoansRefresh, userBodega }) {
  const omitir1 = useSelector((state) => state.omitir1.omitir1);
  const [serialsList, setSerialsList] = useState([]);
  const [serialsPage, setSerialsPage] = useState(1);
  const [serialsTotal, setSerialsTotal] = useState(0);
  const [serialsProductId, setSerialsProductId] = useState(null);
  const [serialsLoanId, setSerialsLoanId] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [generalFileList, setGeneralFileList] = useState([]);
  const [loadingGeneralSerials, setLoadingGeneralSerials] = useState(false);
  const [modalSerials, setModalSerials] = useState(false);
  const [modalSummaryVisible, setModalSummaryVisible] = useState(false);
  const [summarySerials, setSummarySerials] = useState([]);
  const [modalNotFoundVisible, setModalNotFoundVisible] = useState(false);
  const [notFoundSerialsList, setNotFoundSerialsList] = useState([]);
  const [notFoundSerialsPage, setNotFoundSerialsPage] = useState(1);
  const notFoundSerialsPageSize = 200;
  const pageSize = 200;

  // Obtener seriales de un producto/prestamo
  const obtenerSerialesProducto = async (
    productoId,
    prestamoId,
    page = 1,
    pageSize = 200
  ) => {
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readItems("seriales", {
          fields: [
            "id",
            "serial",
            "estado",
            "producto.id",
            "producto.grupo.impuesto",
            "producto.preciosinimp",
            "producto.nombre",
          ],
          filter: {
            _and: [
              {
                prestamo: {
                  prestamos_id: {
                    id: { _eq: prestamoId },
                  },
                },
              },
              { estado: { _eq: 3 } },
              { user: { _eq: vendor.id } },
              { producto: { id: { _eq: productoId } } },
            ],
          },
          limit: pageSize,
          offset: (page - 1) * pageSize,
        })
      );
      const responseCount = await directus.request(
        readItems("seriales", {
          filter: {
            _and: [
              {
                prestamo: {
                  prestamos_id: {
                    id: { _eq: prestamoId },
                  },
                },
              },
              { estado: { _eq: 3 } },
              { user: { _eq: vendor.id } },
              { producto: { id: { _eq: productoId } } },
            ],
          },
          aggregate: { countDistinct: ["id"] },
        })
      );
      setSerialsTotal(responseCount[0]?.countDistinct?.id || 0);
      return response.data || response;
    } catch (error) {
      console.error("Error obteniendo seriales:", error);
      message.error("Error al obtener los seriales");
      return [];
    }
  };

  // Handler para ver seriales de un producto/prestamo
  const onViewSerials = async (record) => {
    setSerialsPage(1);
    setSerialsProductId(record.productId);
    setSerialsLoanId(record.loanId);
    const serials = await obtenerSerialesProducto(
      record.productId,
      record.loanId,
      1,
      pageSize
    );
    setSerialsList(serials);
    setModalSerials(true);
  };

  // Handler para paginación de seriales
  const onSerialsPageChange = async (page) => {
    setSerialsPage(page);
    if (serialsProductId && serialsLoanId) {
      const serials = await obtenerSerialesProducto(
        serialsProductId,
        serialsLoanId,
        page,
        pageSize
      );
      setSerialsList(serials);
    }
  };

  // Handler para cerrar modal de seriales
  const onCloseSerialsModal = () => {
    setSerialsList([]);
    setModalSerials(false);
  };

  // Handler para procesar archivo de seriales por producto
  const onUploadSerials = async (file, record) => {
    const serials = await obtenerSerialesProducto(
      record.productId,
      record.loanId,
      1,
      10000
    );
    await processFile(file, serials);
    return false;
  };

  // Procesar archivo de seriales para devolución por producto
  const processFile = async (file, serialsProducto) => {
    try {
      const text = await file.text();
      const serialNumbers = text
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map((serial) => truncateSerial(serial, omitir1));
      const serialsReturned = serialsProducto.filter(
        (s) => serialNumbers.includes(s.serial) && s.estado === 1
      );
      if (serialsReturned.length > 0) {
        Modal.warning({
          title: "Seriales ya devueltos",
          content: `Los siguientes seriales ya fueron devueltos: ${serialsReturned
            .map((s) => s.serial)
            .join(", ")}`,
        });
        return;
      }
      const productSerials = new Set(serialsProducto.map((s) => s.serial));
      const invalidSerials = serialNumbers.filter(
        (serial) => !productSerials.has(serial)
      );
      if (invalidSerials.length > 0) {
        Modal.error({
          title: "Seriales inválidos",
          content: `Los siguientes seriales no pertenecen a este préstamo: ${invalidSerials.join(", ")}`,
        });
        return;
      }
      const serialsToReturn = serialsProducto.filter((s) =>
        serialNumbers.includes(s.serial)
      );

      if (serialsToReturn.length === 0) {
        Modal.warning({
          title: "No hay seriales para devolver",
          content: "No se encontraron seriales válidos para devolver",
        });
        return;
      }

      // Obtener el ID del usuario actual desde el estado de Redux o localStorage
      const userCreated = localStorage.getItem('userId') || vendor.id; // Fallback al vendor.id si no hay userId

      // Llamar al endpoint de la API para devolver seriales
      const result = await callRepayAPI('/api/repay', {
        vendorId: vendor.id,
        serials: serialNumbers,
        user_created: userCreated
      });

      // Mostrar mensaje con el conteo correcto
      if (result.successfullyReturned !== undefined) {
        Modal.success({
          title: "Devolución completada",
          content: `${result.successfullyReturned} de ${result.totalSent} seriales devueltos correctamente. ${result.notFoundCount} seriales no se encontraron.`,
        });
      } else {
        Modal.success({
          title: "Éxito",
          content: `${serialsToReturn.length} seriales devueltos correctamente`,
        });
      }

      await onLoansRefresh();
    } catch (error) {
      console.error("Error processing file:", error);
      
      // Manejar específicamente el error de seriales no encontrados
      if (error.message.includes('Seriales no encontrados:')) {
        Modal.error({
          title: "Seriales no encontrados",
          content: error.message,
        });
      } else if (error.message.includes('404')) {
        Modal.error({
          title: "Seriales no pertenecen al vendedor",
          content: "Los seriales proporcionados no pertenecen al vendedor seleccionado o no están prestados.",
        });
      } else {
        Modal.error({
          title: "Error",
          content: "Error al procesar el archivo: " + (error.message || "Error desconocido"),
        });
      }
    }
  };

  // Handler para devolución de productos no serializados
  const onReturnNotSerialized = async (record) => {
    try {
      const directus = await getDirectusClient();
      
      // Primero, obtener todos los préstamos no serializados del vendedor
      const inventarioNSPrestamo = await directus.request(
        readItems("inventario_ns_prestamo", {
          fields: ["*", "inventario_ns.*", "inventario_ns.producto.*"],
          filter: {
            _and: [
              {
                prestamos: {
                  prestamos_id: {
                    vendedor: {
                      _eq: vendor.id,
                    },
                  },
                },
              },
              {
                estado: {
                  _eq: "prestado",
                },
              },
            ],
          },
        })
      );
      
      if (!inventarioNSPrestamo || inventarioNSPrestamo.length === 0) {
        message.error("No se encontraron préstamos no serializados");
        return;
      }
      
      // Buscar el préstamo que coincida con el producto del record
      const prestamoEncontrado = inventarioNSPrestamo.find(
        prestamo => prestamo.inventario_ns?.producto?.id === record.productId
      );
      
      if (!prestamoEncontrado) {
        console.log("Record productId:", record.productId);
        console.log("Préstamos disponibles:", inventarioNSPrestamo.map(p => ({
          id: p.id,
          productId: p.inventario_ns?.producto?.id,
          productName: p.inventario_ns?.producto?.nombre,
          cantidad: p.cantidad
        })));
        message.error("No se encontró el préstamo del producto específico");
        return;
      }
      
      const prestamo = prestamoEncontrado;
      const cantidadDevuelta = prestamo.cantidad; // Esta es la cantidad prestada
      
      // Obtener el ID del producto desde la relación correcta
      const productId = prestamo.inventario_ns?.producto?.id;
      
      console.log("Datos del préstamo:", {
        prestamoId: prestamo.id,
        cantidadPrestada: prestamo.cantidad,
        cantidadInventario: prestamo.inventario_ns?.cantidad,
        productId: productId,
        productName: prestamo.inventario_ns?.producto?.nombre,
        estado: prestamo.estado
      });
      
      if (!productId) {
        message.error("No se pudo obtener el ID del producto");
        return;
      }

      // Obtener el ID del usuario actual desde el estado de Redux o localStorage
      const userCreated = localStorage.getItem('userId') || vendor.id; // Fallback al vendor.id si no hay userId
      
      console.log("Producto a devolver:", {
        vendorId: vendor.id,
        productId: productId,
        quantity: cantidadDevuelta,
        user_created: userCreated
      });

      // Llamar al endpoint de la API para devolver productos no serializados
      await callRepayAPI('/api/repay-non-serialized', {
        vendorId: vendor.id,
        productId: productId,
        quantity: cantidadDevuelta,
        user_created: userCreated
      });

      message.success("Producto devuelto correctamente");
      await onLoansRefresh();
    } catch (error) {
      console.error("Error al devolver producto:", error);
      message.error("Error al devolver el producto: " + (error.message || "Error desconocido"));
    }
  };

  // Handler para devolver todos los productos serializados
  const onReturnAllSerialized = async () => {
    setLoadingGeneralSerials(true);
    try {
      // Obtener todos los seriales prestados del vendedor
      const directus = await getDirectusClient();
      const allSerials = await directus.request(
        readItems("seriales", {
          fields: [
            "id",
            "serial",
            "estado",
            "producto.id",
            "producto.nombre",
          ],
          filter: {
            _and: [
              {
                prestamo: {
                  prestamos_id: {
                    vendedor: {
                      _eq: vendor.id,
                    },
                  },
                },
              },
              { estado: { _eq: 3 } },
              { user: { _eq: vendor.id } },
            ],
          },
          limit: -1,
        })
      );

      if (!allSerials || allSerials.length === 0) {
        Modal.warning({
          title: "No hay seriales para devolver",
          content: "No se encontraron seriales prestados para este vendedor.",
        });
        setLoadingGeneralSerials(false);
        return;
      }

      const serialNumbers = allSerials.map(serial => truncateSerial(serial.serial, omitir1));

      // Obtener el ID del usuario actual desde el estado de Redux o localStorage
      const userCreated = localStorage.getItem('userId') || vendor.id;

      // Llamar al endpoint de la API para devolver todos los seriales
      const result = await callRepayAPI('/api/repay', {
        vendorId: vendor.id,
        serials: serialNumbers,
        user_created: userCreated
      });

      // Mostrar mensaje con el conteo correcto
      if (result.successfullyReturned !== undefined) {
        Modal.success({
          title: "Devolución completada",
          content: `${result.successfullyReturned} de ${result.totalSent} seriales devueltos correctamente. ${result.notFoundCount} seriales no se encontraron.`,
        });
      } else {
        Modal.success({
          title: "Éxito",
          content: `${serialNumbers.length} seriales devueltos correctamente`,
        });
      }

      await onLoansRefresh();
      setLoadingGeneralSerials(false);
    } catch (error) {
      console.error("Error al devolver todos los seriales:", error);
      
      // Manejar específicamente el error de seriales no encontrados
      if (error.message.includes('Seriales no encontrados:')) {
        Modal.error({
          title: "Seriales no encontrados",
          content: error.message,
        });
      } else if (error.message.includes('404')) {
        Modal.error({
          title: "Error",
          content: "Los seriales no pertenecen al vendedor seleccionado o no están prestados.",
        });
      } else {
        Modal.error({
          title: "Error",
          content: "Error al devolver los seriales: " + (error.message || "Error desconocido"),
        });
      }
      setLoadingGeneralSerials(false);
    }
  };

  // Handler para subir archivo de seriales a nivel general
  const onGeneralSerialsUpload = async (file) => {
    setLoadingGeneralSerials(true);
    try {
      const text = await file.text();
      const serialNumbers = text
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map((serial) => truncateSerial(serial, omitir1));

      if (serialNumbers.length === 0) {
        Modal.warning({
          title: "Archivo vacío",
          content: "El archivo no contiene seriales válidos",
        });
        setLoadingGeneralSerials(false);
        return;
      }

      // Obtener el ID del usuario actual desde el estado de Redux o localStorage
      const userCreated = localStorage.getItem('userId') || vendor.id; // Fallback al vendor.id si no hay userId

      // Llamar al endpoint de la API para devolver seriales
      const result = await callRepayAPI('/api/repay', {
        vendorId: vendor.id,
        serials: serialNumbers,
        user_created: userCreated
      });

      // Mostrar mensaje con el conteo correcto
      if (result.successfullyReturned !== undefined) {
        Modal.success({
          title: "Devolución completada",
          content: `${result.successfullyReturned} de ${result.totalSent} seriales devueltos correctamente. ${result.notFoundCount} seriales no se encontraron.`,
        });
      } else {
        Modal.success({
          title: "Éxito",
          content: `${serialNumbers.length} seriales procesados correctamente`,
        });
      }

      setGeneralFileList([]);
      await onLoansRefresh();
      setLoadingGeneralSerials(false);
    } catch (error) {
      console.error("Error al procesar seriales generales:", error);
      
      // Manejar específicamente el error de seriales no encontrados
      if (error.message.includes('Seriales no encontrados:')) {
        Modal.error({
          title: "Seriales no encontrados",
          content: error.message,
        });
      } else if (error.message.includes('404')) {
        Modal.error({
          title: "Seriales no pertenecen al vendedor",
          content: "Los seriales proporcionados no pertenecen al vendedor seleccionado o no están prestados.",
        });
      } else {
        Modal.error({
          title: "Error",
          content: "Error al procesar el archivo de seriales: " + (error.message || "Error desconocido"),
        });
      }
      setLoadingGeneralSerials(false);
    }
  };

  // Handlers para cerrar modales
  const onCloseSummaryModal = () => setModalSummaryVisible(false);
  const onCloseNotFoundModal = () => setModalNotFoundVisible(false);

  return {
    serialsList,
    setSerialsList,
    serialsPage,
    setSerialsPage,
    serialsTotal,
    setSerialsTotal,
    serialsProductId,
    setSerialsProductId,
    serialsLoanId,
    setSerialsLoanId,
    fileList,
    setFileList,
    generalFileList,
    setGeneralFileList,
    loadingGeneralSerials,
    setLoadingGeneralSerials,
    modalSerials,
    setModalSerials,
    modalSummaryVisible,
    setModalSummaryVisible,
    summarySerials,
    setSummarySerials,
    modalNotFoundVisible,
    setModalNotFoundVisible,
    notFoundSerialsList,
    setNotFoundSerialsList,
    notFoundSerialsPage,
    setNotFoundSerialsPage,
    notFoundSerialsPageSize,
    onViewSerials,
    onUploadSerials,
    onReturnNotSerialized,
    onReturnAllSerialized,
    onGeneralSerialsUpload,
    onCloseSerialsModal,
    onSerialsPageChange,
    onCloseSummaryModal,
    onCloseNotFoundModal,
  };
} 